import { useCallback, useEffect, useState } from 'react'
import React from 'react'
//@ts-ignore
import Overlay from 'ol/Overlay'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import Feature from 'ol/Feature'
import Point from 'ol/geom/Point'
import { Style, Stroke, Fill, Circle as CircleStyle } from 'ol/style'
import useMap from 'hooks/use-map'
import * as c from 'common/c'
import { get } from 'es-toolkit/compat'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { trackEvent } from 'api/usage-tracking'

import useMapCoordinate from 'hooks/use-click-coordinate'
import { pointInfoReq } from 'actions/map'
import PointInfoView from 'components/map/point-info-view/index'
import PopupInternal from 'components/popup/popup-internal'
import { colors } from 'components/foundation'
import { TEST_IDS } from 'test-ids'

export default function PointInfo() {
  const dispatch = useDispatch()

  const { t } = useTranslation()
  const [overlay, setOverlay] = useState<Overlay | null | undefined>(null)
  const [markerLayer, setMarkerLayer] =
    useState<VectorLayer<VectorSource> | null>(null)
  const map = useMap()
  const data = useSelector((state) => get(state, 'map.pointInfoRes'))

  const coordinate = useMapCoordinate()

  useEffect(() => {
    // Tracking event: only once per session
    if (!sessionStorage.getItem('umami_point_inspect_fired')) {
      trackEvent('point-inspection-tool')
      sessionStorage.setItem('umami_point_inspect_fired', '1')
    }

    dispatch(pointInfoReq(coordinate))
  }, [coordinate])

  useEffect(() => {
    if (!map || !coordinate || (coordinate[0] === 0 && coordinate[1] === 0)) {
      return
    }

    const markerFeature = new Feature({
      geometry: new Point(coordinate),
    })

    const newMarkerLayer = new VectorLayer({
      source: new VectorSource({
        features: [markerFeature],
      }),
      style: new Style({
        image: new CircleStyle({
          radius: 6,
          fill: new Fill({ color: colors.brand }),
          stroke: new Stroke({
            color: colors.white,
            width: 2,
          }),
        }),
      }),
      zIndex: 1000,
    })

    map.addLayer(newMarkerLayer)
    setMarkerLayer(newMarkerLayer)

    return () => {
      map.removeLayer(newMarkerLayer)
    }
  }, [map, coordinate])

  const onClose = useCallback(() => {
    //position is map coordinates, if undefined hide the overlay.
    //actually removing the overlay unless this component unmounts is bad
    // and breaks showing new point info until it unmounts and remounts.
    if (overlay != null) {
      overlay.setPosition(undefined)
    }
    if (map && markerLayer) {
      map.removeLayer(markerLayer)
      setMarkerLayer(null)
    }
  }, [overlay, data, map, markerLayer])

  //We never call remove overlay because that breaks the popup in the future
  useEffect(() => {
    if (map) {
      const currentOverlays = [...map.getOverlays().getArray()]
      //cleanup overlays otherwise we end up with a point info dialog in the top left
      currentOverlays.forEach((o) => {
        if (o !== overlay && 'id' in o && o.id === TEST_IDS.pointInfoPopup) {
          map.removeOverlay(o)
        }
      })
      if (data != c.UNDEFINED) {
        const el = document.getElementById(TEST_IDS.pointInfoPopup)
        if (overlay == null && el) {
          const _overlay = new Overlay({
            element: el,
            position: coordinate,
            offset: [-288, -72],
            id: TEST_IDS.pointInfoPopup,
          })
          setOverlay(_overlay)
        } else if (overlay != null) {
          overlay.setPosition(coordinate)
        }
      }
    }
  }, [map, coordinate, overlay, data])

  useEffect(() => {
    if (map && overlay) {
      map.addOverlay(overlay)
    }
  }, [map, overlay])

  return (
    <div>
      <div id={TEST_IDS.pointInfoPopup} data-testid={TEST_IDS.pointInfoPopup}>
        {overlay != null ? (
          <PopupInternal
            key={'point-info-popup'}
            onClose={onClose}
            title={t('point_data')}
            pixel={{ x: 5, y: 20 }}
            width={272}
            style={{ position: 'absolute' }}
          >
            <PointInfoView />
          </PopupInternal>
        ) : null}
      </div>
    </div>
  )
}
